import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FormattedMessage, useIntl } from "react-intl";
import _debounce from "lodash/debounce";
import { useSelector } from "react-redux";
import { ChevronDown, Info, MapPin } from "lucide-react";
import { searchPlaces } from "bundles/common/utils/locationCoordinates";
import SearchIcon from "../../../../../../app/assets/images/icons/search.svg";
import LocationIcon from "../../../../../../app/assets/images/icons/location_pin.svg";

const useStyles = makeStyles((theme) => ({
  searchContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "40px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  searchInputWrapper: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    borderRadius: "20px",
    padding: "4px 16px",
    width: "100%",
    height: "48px",
    [theme.breakpoints.up("md")]: {
      width: "445px",
    },
  },
  searchInput: {
    flex: 1,
    "& .MuiInputBase-root": {
      border: "none",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiInputBase-input": {
      padding: "8px 0",
      color: theme.palette.text.light_grey,
      "&::placeholder": {
        opacity: 0.9,
      },
    },
  },
  advancedFilterButton: {
    backgroundColor: theme.palette.text.main,
    color: theme.palette.common.white,
    borderRadius: "30px",
    padding: "12px 182px",
    textTransform: "none",
    whiteSpace: "nowrap",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: theme.palette.secondary.hover,
      color: theme.palette.text.main,
    },
  },
}));

const DISTANCE_OPTIONS = [0, 10, 25, 50, 100, 150];
const DEFAULT_TOWN_RADIUS_KM = 25;

const shortTownLabel = (label) => (label || "").split(",")[0].trim();

/**
 * CVthèque search bar: free-text query, and the location filter — countries and
 * regions from our list plus Mapbox towns as you type, several places, one radius.
 */
const CVThequeSearchBar = ({
  queryText,
  setQueryText,
  locationOptions = [],
  selectedLocations,
  setSelectedLocations,
  selectedRadiusKm,
  setSelectedRadiusKm,
  onAdvancedFiltersClick,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const [unboundedQueryText, setUnboundedQueryText] = useState(queryText);
  const [locationInput, setLocationInput] = useState("");
  const [townOptions, setTownOptions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [radiusAnchor, setRadiusAnchor] = useState(null);
  const locationWrapperRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (locationWrapperRef.current && !locationWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const text = locationInput.trim();
    if (text.length < 2) {
      setTownOptions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchPlaces(text, i18nLocale).then((places) => {
        if (cancelled) return;
        setTownOptions(places.map((place) => ({
          code: `town:${place.label}`,
          label: place.label,
          lat: place.lat,
          lng: place.lng,
          type: "town",
        })));
      });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [locationInput]);

  const debouncedSetQueryText = useCallback(
    _debounce((value) => {
      setQueryText(value);
    }, 600),
    [setQueryText]
  );

  const handleQueryChange = (e) => {
    setUnboundedQueryText(e.target.value);
    debouncedSetQueryText(e.target.value);
  };

  useEffect(() => {
    setUnboundedQueryText(queryText || "");
  }, [queryText]);

  const applyLocations = (next) => {
    const countries = next.filter((o) => o?.type === "country");
    const implied = new Set(countries.flatMap((c) => c.regionCodes || []));
    const regions = next.filter((o) => o?.type === "region" && !implied.has(o.code));
    const towns = next.filter((o) => o?.type === "town");
    const locations = [...countries, ...regions, ...towns];
    setSelectedLocations(locations);
    if (locations.length === 0) setSelectedRadiusKm(0);
  };

  const selected = selectedLocations || [];
  const selectedCodes = new Set(selected.map((o) => o.code));
  const needle = locationInput.trim().toLowerCase();
  const localMatches = needle
    ? locationOptions.filter((o) => !selectedCodes.has(o.code) && o.label.toLowerCase().includes(needle)).slice(0, 6)
    : [];
  const suggestions = [...localMatches, ...townOptions.filter((o) => !selectedCodes.has(o.code))];

  const selectLocation = (option) => {
    applyLocations([...selected, option]);
    if (option.type === "town" && !(selectedRadiusKm > 0)) setSelectedRadiusKm(DEFAULT_TOWN_RADIUS_KM);
    setLocationInput("");
    setTownOptions([]);
    setShowSuggestions(false);
  };

  const radiusKm = selectedRadiusKm || 0;
  const radiusPlaces = selected.filter((o) => o?.type !== "country");
  const radiusPlaceNames = radiusPlaces
    .map((o) => (o.type === "town" ? shortTownLabel(o.label) : o.label))
    .join(", ");

  return (
    <Box>
      <Box className={classes.searchContainer}>
        <Box className={classes.searchInputWrapper}>
          <TextField
            className={classes.searchInput}
            placeholder={intl.formatMessage({
              id: "recruiter.cv_theque.search_candidates",
              defaultMessage: "Search candidates",
            })}
            value={unboundedQueryText}
            onChange={handleQueryChange}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <img src={SearchIcon} alt="search" style={{ width: 24, height: 24 }} />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { paddingLeft: "0 !important" } }}
          />
        </Box>

        <Box className={classes.searchInputWrapper} sx={{ position: "relative", gap: "8px" }} ref={locationWrapperRef}>
          <img src={LocationIcon} alt="location" style={{ width: 24, height: 24 }} />
          <Box
            component="input"
            value={locationInput}
            onChange={(e) => { setLocationInput(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            placeholder={intl.formatMessage({ id: "recruiter.cv_theque.location", defaultMessage: "City, region or country" })}
            sx={{
              flex: 1, minWidth: 0, border: "none", outline: "none", bgcolor: "transparent",
              fontSize: "16px", fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              color: "hsl(178, 93%, 10%)",
              "&::placeholder": { color: "hsl(178, 10%, 55%)", opacity: 1 },
            }}
          />
          {radiusPlaces.length > 0 && (
            <Box
              component="button"
              type="button"
              aria-haspopup="menu"
              onClick={(e) => setRadiusAnchor(e.currentTarget)}
              sx={{
                display: "flex", alignItems: "center", gap: "4px", flexShrink: 0,
                pl: "12px", ml: "4px", py: "4px", pr: 0,
                border: "none", borderLeft: "1px solid hsl(160, 10%, 90%)", bgcolor: "transparent",
                cursor: "pointer", whiteSpace: "nowrap",
                fontSize: "14px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                color: radiusKm > 0 ? "hsl(178, 93%, 10%)" : "hsl(178, 10%, 45%)",
                "&:hover": { color: "hsl(178, 93%, 10%)" },
              }}
            >
              {radiusKm > 0
                ? `+ ${radiusKm} km`
                : intl.formatMessage({ id: "recruiter.cv_theque.radius_label", defaultMessage: "Radius" })}
              <ChevronDown size={14} />
            </Box>
          )}
          {radiusPlaces.length > 0 && (
            <Tooltip
              placement="top"
              title={intl.formatMessage(
                { id: "recruiter.cv_theque.radius_note", defaultMessage: "Radius measured from the centre of {places}" },
                { places: radiusPlaceNames }
              )}
            >
              <Box
                component="button"
                type="button"
                aria-label={intl.formatMessage(
                  { id: "recruiter.cv_theque.radius_note", defaultMessage: "Radius measured from the centre of {places}" },
                  { places: radiusPlaceNames }
                )}
                sx={{
                  position: "absolute", top: "-8px", right: "-8px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: "20px", height: "20px", p: 0, borderRadius: "50%",
                  border: "1px solid hsl(160, 10%, 90%)", bgcolor: "hsl(0, 0%, 100%)",
                  color: "hsl(178, 10%, 45%)", cursor: "help",
                  boxShadow: "0 2px 6px hsla(178, 93%, 10%, 0.08)",
                  "&:hover": { color: "hsl(178, 93%, 10%)" },
                }}
              >
                <Info size={12} />
              </Box>
            </Tooltip>
          )}
          <Menu
            anchorEl={radiusAnchor}
            open={Boolean(radiusAnchor)}
            onClose={() => setRadiusAnchor(null)}
            disableScrollLock
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { mt: "8px", borderRadius: "12px", minWidth: "120px", boxShadow: "0 4px 16px hsla(178, 93%, 10%, 0.08)" } }}
          >
            {DISTANCE_OPTIONS.map((km) => (
              <MenuItem
                key={km}
                selected={radiusKm === km}
                onClick={() => {
                  setSelectedRadiusKm(km);
                  setRadiusAnchor(null);
                }}
                sx={{ fontSize: "13px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(178, 93%, 10%)" }}
              >
                {km === 0
                  ? intl.formatMessage({ id: "recruiter.cv_theque.radius_all", defaultMessage: "All" })
                  : `${km} km`}
              </MenuItem>
            ))}
          </Menu>
          {showSuggestions && suggestions.length > 0 && (
            <Box sx={{
              position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, zIndex: 30,
              bgcolor: "hsl(0, 0%, 100%)", border: "1px solid hsl(160, 10%, 90%)",
              borderRadius: "16px", p: "6px",
              boxShadow: "0 4px 16px hsla(178, 93%, 10%, 0.08)",
              maxHeight: "300px", overflowY: "auto",
            }}>
              {suggestions.map((option) => (
                <Box
                  key={option.code}
                  component="button"
                  onClick={() => selectLocation(option)}
                  sx={{
                    display: "flex", alignItems: "center", gap: "8px", width: "100%",
                    px: "10px", py: "8px", borderRadius: "10px", border: "none", cursor: "pointer",
                    bgcolor: "transparent", textAlign: "left",
                    fontSize: "13px", fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    color: "hsl(178, 93%, 10%)",
                    "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.8)" },
                  }}
                >
                  <MapPin size={12} color="hsl(178, 10%, 50%)" style={{ flexShrink: 0 }} />
                  <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: option.type === "country" ? "Segoe UI Bold, sans-serif, system-ui" : undefined }}>
                    {option.label}
                  </Box>
                  {option.type !== "town" && (
                    <Box component="span" sx={{ ml: "auto", fontSize: "11px", color: "hsl(178, 10%, 50%)", flexShrink: 0 }}>
                      {option.type === "country" ? "" : option.country}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Box display="flex" justifyContent="center" mb={4}>
        <Button
          className={classes.advancedFilterButton}
          onClick={onAdvancedFiltersClick}
          sx={{ fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}
        >
          <FormattedMessage id="recruiter.cv_theque.advanced_filters" defaultMessage="Advanced filters" />
        </Button>
      </Box>
    </Box>
  );
};

export default CVThequeSearchBar;
